import { abbreviateState } from './abbreviate-state';

/**
 * Suffixes that indicate a "city" value is actually a broader administrative
 * area (seen in PMS feeds, e.g. "Walton County"). These are not useful as a
 * city label on a listing card, so we fall back to the state instead.
 */
const NON_CITY_SUFFIXES = [' county', ' parish', ' borough', ' census area'];

function isNonCityValue(city: string): boolean {
  const lower = city.toLowerCase();
  return NON_CITY_SUFFIXES.some((suffix) => lower.endsWith(suffix));
}

/**
 * PMS feeds sometimes send city names in all caps ("INLET BEACH"). Title-case
 * those; leave mixed-case values untouched so intentional casing survives
 * (e.g. "McHenry", "La Jolla").
 */
function normalizeCasing(city: string): string {
  if (city !== city.toUpperCase()) return city;
  return city
    .toLowerCase()
    .replace(/(^|[\s\-'])\p{L}/gu, (char) => char.toUpperCase());
}

/**
 * Normalizes a PMS-supplied city value for display. Returns undefined when
 * the value is missing or is not actually a city (e.g. "Walton County").
 */
export function normalizeCity(
  city: string | null | undefined
): string | undefined {
  const trimmed = city?.trim();
  if (!trimmed) return undefined;
  if (isNonCityValue(trimmed)) return undefined;
  return normalizeCasing(trimmed);
}

/**
 * Composes the short location line shown on listing cards ("City, ST").
 *
 * Skips missing parts instead of stringifying them, so a null city renders
 * as "FL" rather than the literal "null, FL". City values are normalized:
 * trimmed, title-cased when ALL CAPS, and county-only values are treated as
 * missing.
 */
export function formatListingLocation(
  city: string | null | undefined,
  stateOrProvinceLongName: string | null | undefined
): string {
  return [
    normalizeCity(city),
    abbreviateState(stateOrProvinceLongName ?? undefined),
  ]
    .filter(Boolean)
    .join(', ');
}
